/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Play } from 'lucide-react';
import { useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import StaticMarkdown from '@/components/charts/StaticMarkdown';
import StaticMermaidDiagram from '@/components/charts/StaticMermaidDiagram';
import StaticStoredPlotlyPlot from '@/components/charts/StaticStoredPlotlyPlot';

/* Import libraries */
import { ChartConfigDetector, type ChartConfigType } from '@/lib/conversion/ChartConfigDetector';
import { ChartJsToPlotlyConverter, type ChartJsConfiguration } from '@/lib/conversion/ChartJsToPlotlyConverter';

/* Import types */
import type { StoredPlotlyResponse } from '@/types/eeCheck';

export type StaticRendererDemoType = 'markdown' | 'mermaid' | 'plotly';

interface StaticRendererDemoTabProps {
    renderer: StaticRendererDemoType;
    theme: 'light' | 'dark';
}

interface StaticRendererDemoConfig {
    defaultSource: string;
    description: string;
    inputLabel: string;
    title: string;
}

const STATIC_RENDERER_DEMO_CONFIGS: Record<StaticRendererDemoType, StaticRendererDemoConfig> = {
    markdown: {
        defaultSource: `# Markdown preview

Enter Markdown in the editor and use **Render** to update this preview.

- Headings
- Lists
- Tables

can be rendered directly in the application.`,
        description: 'Enter Markdown text and render it with the shared StaticMarkdown component.',
        inputLabel: 'Markdown source',
        title: 'Markdown',
    },
    mermaid: {
        defaultSource: `flowchart LR
    Input[Mermaid source] --> Render[Render diagram]
    Render --> Diagram[SVG diagram]`,
        description: 'Enter a Mermaid definition and render it with the shared StaticMermaidDiagram component.',
        inputLabel: 'Mermaid source',
        title: 'Mermaid',
    },
    plotly: {
        defaultSource: `{
    "data": [
        {
            "x": ["Mon", "Tue", "Wed", "Thu", "Fri"],
            "y": [12, 18, 15, 20, 17],
            "type": "bar",
            "name": "Example values"
        }
    ],
    "layout": {
        "title": {
            "text": "Plotly preview"
        },
        "yaxis": {
            "title": {
                "text": "Value"
            }
        }
    }
}`,
        description: 'Enter Plotly JSON or a Chart.js configuration and render it with the shared StaticStoredPlotlyPlot component.',
        inputLabel: 'Plotly or Chart.js JSON',
        title: 'Plotly',
    },
};

interface PlotlySourceValidation {
    figure: StoredPlotlyResponse | null;
    type: ChartConfigType;
}

const chartJsToPlotlyConverter = new ChartJsToPlotlyConverter();

function validatePlotlySource(source: string): PlotlySourceValidation {
    try {
        const parsedSource: unknown = JSON.parse(source);
        const type = ChartConfigDetector.detect(parsedSource);

        if (type === 'chartjs') {
            return {
                figure: chartJsToPlotlyConverter.convert(parsedSource as ChartJsConfiguration),
                type,
            };
        }

        return {
            figure: type === 'plotly' ? parsedSource as StoredPlotlyResponse : null,
            type,
        };
    } catch {
        return {
            figure: null,
            type: 'unknown',
        };
    }
}

function StaticRendererDemoTab({ renderer, theme }: StaticRendererDemoTabProps) {
    const { t } = useI18n();
    const config = STATIC_RENDERER_DEMO_CONFIGS[renderer];
    const initialPlotlySource = renderer === 'plotly'
        ? validatePlotlySource(config.defaultSource)
        : null;
    const [source, setSource] = useState(config.defaultSource);
    const [renderedSource, setRenderedSource] = useState(config.defaultSource);
    const [plotlyFigure, setPlotlyFigure] = useState<StoredPlotlyResponse | null>(initialPlotlySource?.figure ?? null);
    const [plotlySourceType, setPlotlySourceType] = useState<ChartConfigType>(initialPlotlySource?.type ?? 'unknown');
    const isInvalidPlotlySource = renderer === 'plotly' && plotlySourceType === 'unknown';
    const detectedPlotlyFormat = plotlySourceType === 'chartjs'
        ? 'Chart.js'
        : plotlySourceType === 'plotly'
            ? t('Plotly JSON')
            : t('Invalid chart configuration');

    const updateSource = (nextSource: string) => {
        setSource(nextSource);

        if (renderer === 'plotly') {
            setPlotlySourceType(validatePlotlySource(nextSource).type);
        }
    };

    const handleBeautifyJson = () => {
        const parsedSource: unknown = JSON.parse(source);

        updateSource(JSON.stringify(parsedSource, null, 4));
    };

    const handleConvertToPlotlyJson = () => {
        const convertedSource = validatePlotlySource(source).figure;

        if (convertedSource) {
            updateSource(JSON.stringify(convertedSource, null, 4));
        }
    };

    const handleRender = () => {
        if (renderer === 'plotly') {
            const nextPlotlySource = validatePlotlySource(source);

            if (nextPlotlySource.figure === null) {
                setPlotlySourceType('unknown');
                return;
            }

            setPlotlySourceType(nextPlotlySource.type);
            setPlotlyFigure(nextPlotlySource.figure);
        }

        setRenderedSource(source);
    };

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t(config.title)}</h2>
                <p>{t(config.description)}</p>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="flex w-full flex-col gap-3">
                    <label className="text-sm font-semibold" htmlFor={`${renderer}-renderer-source`}>
                        {t(config.inputLabel)}
                    </label>
                    <textarea
                        id={`${renderer}-renderer-source`}
                        className="textarea textarea-bordered min-h-72 w-full font-mono text-sm"
                        spellCheck={false}
                        value={source}
                        onChange={(event) => {
                            updateSource(event.target.value);
                        }}
                    />
                    {renderer === 'plotly' ? (
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className={`mb-0 text-sm${isInvalidPlotlySource ? ' text-error' : ' text-base-content/65'}`}>
                                <strong>{t('Detected format:')}</strong>
                                {' '}
                                {detectedPlotlyFormat}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    disabled={isInvalidPlotlySource}
                                    onClick={handleBeautifyJson}
                                >
                                    {t('Beautify JSON')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    disabled={plotlySourceType !== 'chartjs'}
                                    onClick={handleConvertToPlotlyJson}
                                >
                                    {t('Convert to Plotly JSON')}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            <section className="flex flex-col gap-3">
                <div>
                    <button
                        type="button"
                        className="btn btn-primary gap-2"
                        disabled={isInvalidPlotlySource}
                        onClick={handleRender}
                    >
                        <Play className="h-4 w-4" aria-hidden="true" />
                        <span>{t('Render')}</span>
                    </button>
                </div>
            </section>

            <section className="dashboard-panel dashboard-panel--chart min-h-96">
                {renderer === 'markdown' ? <StaticMarkdown content={renderedSource} /> : null}
                {renderer === 'mermaid' ? (
                    <StaticMermaidDiagram definition={renderedSource} fillParent theme={theme} />
                ) : null}
                {renderer === 'plotly' ? <StaticStoredPlotlyPlot figure={plotlyFigure} fillParent /> : null}
            </section>
        </div>
    );
}

export default StaticRendererDemoTab;
